// API layer for the AI journal draft flow (`/journal-drafts`, see
// je-draft-contract.md). Plain async helpers rather than thunks: the review
// screen is used from several places (chat card, audit modal) and keeps the
// draft in local state, the same way PreviewJournal calls getApi/postApi
// directly.
import { getApi, postApi, putApi } from 'redux/apis';
import type {
	DraftDetail,
	DraftListQuery,
	DraftSummary,
	DraftUpdateBody,
	PostFailure,
	PostResult,
} from 'components/JournalDrafts/types';

const BASE = '/journal-drafts';

const currentCompanyId = (companyId?: string) =>
	companyId || localStorage.getItem('companyId') || '';

// Responses use the DefaultResponse envelope: { message, statusCode, data }.
const unwrap = <T,>(response: any): T => response?.data?.data as T;

export const listJournalDrafts = async (
	query: DraftListQuery,
	companyId?: string
): Promise<{ items: DraftSummary[]; total: number }> => {
	const params: Record<string, unknown> = {
		companyId: currentCompanyId(companyId),
	};
	Object.entries(query).forEach(([key, value]) => {
		if (value !== undefined && value !== null && value !== '') {
			params[key] = value;
		}
	});
	const data = unwrap<{ items: DraftSummary[]; total: number }>(
		await getApi(BASE, params)
	);
	return { items: data?.items ?? [], total: data?.total ?? 0 };
};

export const getJournalDraft = async (
	draftId: string,
	companyId?: string
): Promise<DraftDetail> =>
	unwrap<DraftDetail>(
		await getApi(`${BASE}/${draftId}`, { companyId: currentCompanyId(companyId) })
	);

export const getJournalDraftQboPayload = async (
	draftId: string,
	companyId?: string
): Promise<unknown> => {
	const data = unwrap<{ payload: unknown }>(
		await getApi(`${BASE}/${draftId}/qbo-payload`, {
			companyId: currentCompanyId(companyId),
		})
	);
	return data?.payload;
};

export const approveJournalDraft = async (
	draftId: string,
	body: { version: number; linesHash: string },
	companyId?: string
): Promise<PostResult> =>
	unwrap<PostResult>(
		await postApi(`${BASE}/${draftId}/approve`, {
			companyId: currentCompanyId(companyId),
			version: body.version,
			linesHash: body.linesHash,
		})
	);

export const editJournalDraft = async (
	draftId: string,
	companyId?: string
): Promise<DraftDetail> =>
	unwrap<DraftDetail>(
		await postApi(`${BASE}/${draftId}/edit`, {
			companyId: currentCompanyId(companyId),
		})
	);

export const updateJournalDraft = async (
	draftId: string,
	body: DraftUpdateBody,
	companyId?: string
): Promise<DraftDetail> => {
	const payload: Record<string, unknown> = {
		companyId: currentCompanyId(companyId),
		txnDate: body.txnDate,
		memo: body.memo,
		adjustment: body.adjustment,
	};
	// docNumber is optional — only sent when the user supplied one (QBO
	// auto-assigns otherwise, spec §6).
	if (body.docNumber !== undefined) {
		payload.docNumber = body.docNumber;
	}
	return unwrap<DraftDetail>(await putApi(`${BASE}/${draftId}`, payload));
};

export const declineJournalDraft = async (
	draftId: string,
	reason: string,
	companyId?: string
): Promise<DraftDetail> =>
	unwrap<DraftDetail>(
		await postApi(`${BASE}/${draftId}/decline`, {
			companyId: currentCompanyId(companyId),
			reason,
		})
	);

export const revalidateJournalDraft = async (
	draftId: string,
	companyId?: string
): Promise<DraftDetail | undefined> =>
	unwrap<DraftDetail | undefined>(
		await postApi(`${BASE}/${draftId}/revalidate`, {
			companyId: currentCompanyId(companyId),
		})
	);

// The backend's error middleware responds with
// { error: { status, message, additionalInfo }, message, responseStatus };
// a failed approve carries {stage, message, whatToChange, needsReapproval, draft}
// in additionalInfo. Fall back to other plausible spots and finally to the
// top-level message so a generic error still renders as a failure panel.
export const parseJournalDraftError = (err: any): PostFailure => {
	const body = err?.response?.data ?? {};
	const candidates = [
		body?.error?.additionalInfo,
		body?.error?.detail,
		body?.detail,
		body?.data,
		body?.additionalInfo,
	];
	const detail = candidates.find(
		(c) => c && typeof c === 'object' && (c.stage || c.message || c.draft)
	);
	const message =
		detail?.message ||
		body?.error?.message ||
		body?.message ||
		err?.message ||
		'Something went wrong.';
	return {
		stage: detail?.stage || (err?.response ? 'approval' : 'qbo_posting'),
		message,
		whatToChange: detail?.whatToChange ?? null,
		needsReapproval:
			typeof detail?.needsReapproval === 'boolean' ? detail.needsReapproval : true,
		draft: detail?.draft && detail.draft.id ? (detail.draft as DraftDetail) : undefined,
	};
};
